import { useState, useEffect, useMemo, useRef } from "react";
import { Loader2, ChevronRight, Users, Sparkles, Archive, UserPlus, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CoachLayout } from "@/components/layout/CoachLayout";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useDemoMode } from "@/hooks/useDemoMode";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { formatDistanceToNow, differenceInDays } from "date-fns";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import type { LeaderLifecycleState } from "@/hooks/useLeaderLifecycle";
import { CoachLeaderInviteFlow } from "@/components/coach/CoachLeaderInviteFlow";
import { CoachInvitationsCard } from "@/components/coach/CoachInvitationsCard";
import { demoLeaders, demoSignals } from "@/data/demoData";
import { getAvatarInitials } from "@/utils/avatar";
import { COACH_REVIEWABLE_ANALYSIS_STATUSES, isTranscriptNeedsCoachReview } from "@/lib/transcriptStatus";
import { useCoachNewEvidence } from "@/hooks/useCoachNewEvidence";
import { useLeaderGate } from "@/hooks/useLeaderGate";
import { UpgradeBanner, GracePeriodBadge } from "@/components/coach/UpgradeBanner";
import { buildCoachLeaderListRowViewModel } from "@/lib/behavior/viewModels/buildCoachLeaderListRow";
import { useCoachSurfaceRuntime } from "@/hooks/useCoachSurfaceRuntime";
import { filterUnopenedCoachReviewTranscripts } from "@/lib/coachReviewArtifacts";
import { buildCoachVeeraContextualEntry } from "@/lib/coachVeeraContextualEntry";
import { buildCoachVeeraRoute } from "@/lib/veeraRoutes";
import { buildCoachBillingPendingCopy } from "@/lib/billingOwnership";
import { CoachHealthRecordedContextRow } from "@/components/coach/CoachHealthRecordedContextRow";
import { CoachHealthReflectionWorkspace } from "@/components/coach/CoachHealthReflectionWorkspace";
import { useCoachHealthEvidenceProfile } from "@/hooks/useCoachHealthEvidenceProfile";
import { buildCoachHealthPresentation } from "@/lib/coachHealth/buildCoachHealthPresentation";
import { coachHealthProfileQueryKey } from "@/lib/coachHealth/coachHealthScope";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

interface AttentionItem {
  leaderId: string;
  priority: number;
}

interface LeaderWithBehavior {
  id: string;
  full_name: string;
  email: string;
  lifecycle_state: LeaderLifecycleState;
  behaviorCount: number;
  behavior: {
    id: string;
    name: string;
    theme: string;
    phase: string;
    streak_current: number;
    total_practice_days: number;
    last_practice_date: string | null;
  } | null;
  readiness?: {
    type: "sequencing" | "graduation";
    level: "low" | "moderate" | "high";
    signalCount: number;
  };
  hasEnrichment: boolean;
  hasPendingTranscript: boolean;
  pendingTranscriptCount: number;
  latestPendingTranscriptId: string | null;
  hasPendingGuidance: boolean;
  hasInactivityAlert: boolean;
  daysSinceLastPractice: number | null;
  hasPendingBillingResolution?: boolean;
}

type CoachAlert = {
  leader_id: string;
  alert_type: string;
  dismissed: boolean | null;
  dismissed_until: string | null;
  is_read: boolean | null;
};

type CoachReviewArtifactViewRow = {
  leader_id: string;
  transcript_upload_id: string | null;
};

const INACTIVITY_THRESHOLD_DAYS = 5;
const COACH_HEALTH_WORKSPACE_ID = "coach-health-workspace";
type LeaderFocusFilter = "new-evidence" | "needs-review" | "stale";

function getLeaderFocusFilter(searchParams: URLSearchParams): LeaderFocusFilter | null {
  const focus = searchParams.get("focus");
  if (focus === "new-evidence" || focus === "needs-review" || focus === "stale") {
    return focus;
  }
  return null;
}

export function LeadersPage() {
  const { user, loading: authLoading } = useAuth();
  const { isDemoMode } = useDemoMode();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showArchived, setShowArchived] = useState(false);
  const queryClient = useQueryClient();
  const { newEvidenceByLeaderId } = useCoachNewEvidence();
  const {
    isOverLimit,
    overBy,
    gatedLeaderIds,
    gatedLeaders,
    linkedLeaderIds,
    leaderCount,
    leaderLimit,
    plan,
    softGatedCount,
    hardGatedCount,
    accessFingerprint,
    isLoading: isLeaderGateLoading,
    isError: isLeaderGateError,
  } = useLeaderGate();
  const { coachRuntimeSurface } = useCoachSurfaceRuntime({
    coachId: user?.id,
    contextType: "portfolio",
    enabled: Boolean(user?.id) && !isDemoMode,
  });
  const coachHealth = useCoachHealthEvidenceProfile();
  const { coachingHealthFactualContextV2Enabled } = useFeatureFlags();
  const [isCoachHealthWorkspaceOpen, setCoachHealthWorkspaceOpen] = useState(false);
  const [isAccessRefreshPending, setAccessRefreshPending] = useState(false);
  const isMountedRef = useRef(true);
  const acceptedAccessFingerprintRef = useRef<string | null>(null);
  const accessRefreshGenerationRef = useRef(0);
  const scopedAccessFingerprint = `${user?.id ?? "inactive"}|${accessFingerprint}`;
  const accessFingerprintChanged = acceptedAccessFingerprintRef.current !== null &&
    acceptedAccessFingerprintRef.current !== scopedAccessFingerprint;
  const profileIncludesHardGatedLeader = coachHealth.profile.eligibleLeaderIds.some(
    (leaderId) => gatedLeaders.get(leaderId)?.isHardGated === true,
  );
  const profileIncludesUnlinkedLeader = coachHealth.profile.eligibleLeaderIds.some(
    (leaderId) => !linkedLeaderIds.has(leaderId),
  );
  const coachHealthAccessSuppressed =
    accessFingerprintChanged ||
    isAccessRefreshPending ||
    profileIncludesHardGatedLeader ||
    profileIncludesUnlinkedLeader;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Demo mode data
  const demoLeadersData = useMemo(() => {
    if (!isDemoMode) return null;
    return {
      leaders: demoLeaders.map(dl => ({
        id: dl.id,
        full_name: dl.full_name,
        email: dl.email,
        lifecycle_state: dl.lifecycle_state as LeaderLifecycleState,
        behaviorCount: 1,
        behavior: {
          id: `${dl.id}-behavior`,
          name: dl.behaviorName,
          theme: dl.behaviorTheme,
          phase: dl.phase,
          streak_current: dl.streak,
          total_practice_days: dl.totalPracticeDays,
          last_practice_date: dl.lastPracticeDate,
        },
        hasEnrichment: dl.hasEnrichment,
        hasPendingTranscript: false,
        pendingTranscriptCount: 0,
        latestPendingTranscriptId: null,
        hasPendingGuidance: false,
        hasInactivityAlert: false,
        daysSinceLastPractice: 0,
      })),
      attentionItems: demoSignals.map(s => ({
        leaderId: s.leaderId,
        priority: s.priority,
      })),
    };
  }, [isDemoMode]);

  const { data, isLoading, isFetching: isDirectoryFetching = false } = useQuery({
    queryKey: ["coach-leaders-command", user?.id, isDemoMode],
    queryFn: async () => {
      if (isDemoMode) return demoLeadersData;
      if (!user) throw new Error("No user");

      const { data: leaderLinks, error: leaderLinksError } = await supabase
        .from("coach_leader_links")
        .select("leader_id")
        .eq("coach_id", user.id);

      if (leaderLinksError) throw leaderLinksError;

      const linkedLeaderIds = leaderLinks?.map((link) => link.leader_id) ?? [];
      const { data: profiles, error: profilesError } = linkedLeaderIds.length
        ? await supabase
            .from("profiles")
            .select("id, full_name, email, lifecycle_state")
            .in("id", linkedLeaderIds)
        : { data: [], error: null };

      if (profilesError) throw profilesError;

      if (!profiles || profiles.length === 0) return { leaders: [], attentionItems: [] };

      const leaderIds = profiles.map((p) => p.id);

      // Parallel fetches for efficiency
      const [behaviorsResult, transcriptsResult, reviewArtifactViewsResult, onboardingResult, messagesResult, coachAlertsResult, billingResolutionsResult] = await Promise.all([
        // Active behaviors
        supabase
          .from("leader_behaviors")
          .select(`
            id,
            leader_id,
            phase,
            streak_current,
            total_practice_days,
            last_practice_date,
            behaviors (name, theme)
          `)
          .in("leader_id", leaderIds)
          .in("phase", ["ACTIVE_PRACTICE", "CONSOLIDATION", "SYNTHESIS"]),
        
        // Pending transcript reviews
        supabase
          .from("transcript_uploads")
          .select("id, leader_id, analysis_status, coach_verified, created_at")
          .in("leader_id", leaderIds)
          .in("analysis_status", [...COACH_REVIEWABLE_ANALYSIS_STATUSES]),

        // Transcript reviews the coach already opened
        supabase
          .from("coach_review_artifact_views")
          .select("leader_id, transcript_upload_id")
          .eq("coach_id", user.id)
          .eq("artifact_type", "transcript_review")
          .in("leader_id", leaderIds),
        
        // Onboarding data (for enrichment status)
        supabase
          .from("leader_onboarding")
          .select("leader_id, best_self_narrative, meaning_anchor")
          .in("leader_id", leaderIds),
        
        // Unread messages from leaders (guidance requests)
        supabase
          .from("coach_messages")
          .select("id, leader_id, read, sender_id")
          .eq("coach_id", user.id)
          .eq("read", false)
          .in("sender_id", leaderIds),

        // Coach alerts (used to gate Attention Queue items like inactivity)
        supabase
          .from("coach_alerts")
          .select("id, leader_id, alert_type, dismissed, dismissed_until, is_read, evidence_count, show_count")
          .eq("coach_id", user.id)
          .in("leader_id", leaderIds),

        supabase
          .from("billing_ownership_resolutions")
          .select("leader_id, status, proposed_owner_id")
          .eq("proposed_owner_id", user.id)
          .eq("proposed_owner_type", "coach")
          .eq("status", "pending")
          .in("leader_id", leaderIds),
      ]);

      const behaviors = behaviorsResult.data || [];
      const openedTranscriptIds = new Set(
        ((reviewArtifactViewsResult.data || []) as CoachReviewArtifactViewRow[])
          .map((view) => view.transcript_upload_id)
          .filter((value): value is string => Boolean(value)),
      );
      const transcripts = filterUnopenedCoachReviewTranscripts((transcriptsResult.data || []).filter((t) =>
        isTranscriptNeedsCoachReview(t.analysis_status, t.coach_verified)
      ), openedTranscriptIds);
      const onboarding = onboardingResult.data || [];
      const unreadMessages = messagesResult.data || [];
      const coachAlerts = (coachAlertsResult.data || []) as unknown as CoachAlert[];
      if (billingResolutionsResult.error) throw billingResolutionsResult.error;
      const pendingBillingLeaderIds = new Set((billingResolutionsResult.data || []).map((row) => row.leader_id));

      const now = new Date();
      const isAlertActive = (a: CoachAlert) => {
        // If already read (coach visited the leader detail page), treat as inactive
        if (a.is_read) return false;
        // If temporarily dismissed and still within dismissal period, treat as inactive
        if (a.dismissed && a.dismissed_until) {
          return new Date(a.dismissed_until) <= now;
        }
        // If permanently dismissed, inactive
        if (a.dismissed && !a.dismissed_until) return false;
        return true;
      };
      // Build leader data
      const leadersWithBehaviors: LeaderWithBehavior[] = profiles.map((profile) => {
        const leaderBehaviors = behaviors.filter((b) => b.leader_id === profile.id);
        const primaryBehavior = leaderBehaviors[0];
        const leaderOnboarding = onboarding.find((o) => o.leader_id === profile.id);
        const pendingLeaderTranscripts = transcripts
          .filter((t) => t.leader_id === profile.id)
          .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        const pendingTranscriptCount = pendingLeaderTranscripts.length;
        const hasPendingTranscript = pendingTranscriptCount > 0;
        const latestPendingTranscriptId = pendingLeaderTranscripts[0]?.id ?? null;
        const hasPendingGuidance = unreadMessages.some((m) => m.leader_id === profile.id);

        const hasInactivityAlert = coachAlerts.some(
          (a) =>
            a.leader_id === profile.id &&
            isAlertActive(a) &&
            (a.alert_type === "streak_broken" || a.alert_type === "engagement_drop")
        );

        // Calculate days since last practice
        let daysSinceLastPractice: number | null = null;
        if (primaryBehavior?.last_practice_date) {
          daysSinceLastPractice = differenceInDays(new Date(), new Date(primaryBehavior.last_practice_date));
        }

        // Enrichment = has best_self_narrative OR meaning_anchor
        const hasEnrichment = !!(leaderOnboarding?.best_self_narrative || leaderOnboarding?.meaning_anchor);

        return {
          id: profile.id,
          full_name: profile.full_name || "Unknown",
          email: profile.email || "",
          lifecycle_state: profile.lifecycle_state as LeaderLifecycleState,
          behaviorCount: leaderBehaviors.length,
          behavior: primaryBehavior
            ? {
                id: primaryBehavior.id,
                name: leaderBehaviors.length > 1
                  ? `${primaryBehavior.behaviors?.name || ""} +${leaderBehaviors.length - 1}`
                  : primaryBehavior.behaviors?.name || "",
                theme: primaryBehavior.behaviors?.theme || "",
                phase: primaryBehavior.phase || "",
                streak_current: Math.max(...leaderBehaviors.map((b) => b.streak_current || 0)),
                total_practice_days: leaderBehaviors.reduce((sum, b) => sum + (b.total_practice_days || 0), 0),
                last_practice_date: primaryBehavior.last_practice_date,
              }
            : null,
          hasEnrichment,
          hasPendingTranscript,
          pendingTranscriptCount,
          latestPendingTranscriptId,
          hasPendingGuidance,
          hasInactivityAlert,
          daysSinceLastPractice,
          hasPendingBillingResolution: pendingBillingLeaderIds.has(profile.id),
        };
      });

      // Fetch readiness for leaders with behaviors
      const leadersWithActiveBehaviors = leadersWithBehaviors.filter((l) => l.behavior);
      const readinessPromises = leadersWithActiveBehaviors.map(async (leader) => {
        try {
          const { data } = await supabase.functions.invoke("analyze-behavior-readiness", {
            body: { leaderId: leader.id },
          });
          return {
            leaderId: leader.id,
            type: data?.readiness_type || "sequencing",
            level: data?.readiness_level || "low",
            signalCount: data?.supporting_signals?.length || 0,
          };
        } catch {
          return { leaderId: leader.id, type: "sequencing" as const, level: "low" as const, signalCount: 0 };
        }
      });

      const readinessResults = await Promise.all(readinessPromises);
      const readinessMap = new Map(readinessResults.map((r) => [r.leaderId, { type: r.type, level: r.level, signalCount: r.signalCount }]));

      // Add readiness to leaders
      const leaders = leadersWithBehaviors.map((leader) => ({
        ...leader,
        readiness: readinessMap.get(leader.id),
      }));

      // Build attention queue - ONLY for active leaders
      const attentionItems: AttentionItem[] = [];

      for (const leader of leaders) {
        // Skip non-active leaders - no signals for paused/completed/archived/deleted
        if (leader.lifecycle_state !== "active") continue;
        // Priority 1: Graduation check (high readiness in consolidation/synthesis)
        if (leader.readiness?.level === "high" && leader.readiness?.type === "graduation") {
          attentionItems.push({
            leaderId: leader.id,
            priority: 1,
          });
        }
        // Priority 1b: Sequence check (high readiness in active practice)
        else if (leader.readiness?.level === "high" || leader.readiness?.level === "moderate") {
          attentionItems.push({
            leaderId: leader.id,
            priority: leader.readiness.level === "high" ? 1 : 2,
          });
        }

        // Priority 2: Transcript awaiting review
        if (leader.hasPendingTranscript) {
          attentionItems.push({
            leaderId: leader.id,
            priority: 2,
          });
        }

        // Priority 3: Leader requested guidance (unread message)
        if (leader.hasPendingGuidance) {
          attentionItems.push({
            leaderId: leader.id,
            priority: 3,
          });
        }

        // Priority 4: Inactivity signal
        // IMPORTANT: This must be dismissible via Coach Review Mode.
        // So we only show it when an *active* coach_alert exists.
        if (leader.hasInactivityAlert) {
          attentionItems.push({
            leaderId: leader.id,
            priority: 4,
          });
        }
      }

      // Sort by priority
      attentionItems.sort((a, b) => a.priority - b.priority);

      return { leaders, attentionItems };
    },
    enabled: !!user || isDemoMode,
    staleTime: 1000 * 30,
  });

  const coachHealthPresentation = useMemo(
    () => buildCoachHealthPresentation({
      profile: coachHealth.profile,
      recordedContextFractionEnabled: coachingHealthFactualContextV2Enabled,
      evidenceActionsEnabled: true,
      freshness:
        coachHealth.isFetching ||
        isDirectoryFetching ||
        isAccessRefreshPending ||
        accessFingerprintChanged ||
        profileIncludesHardGatedLeader ||
        profileIncludesUnlinkedLeader
          ? "refreshing"
          : "fresh",
      leaderLabelsById: coachHealth.leaderLabelsById,
      timeZone: coachHealth.timeZone,
    }),
    [
      coachHealth.isFetching,
      coachHealth.leaderLabelsById,
      coachHealth.profile,
      coachHealth.timeZone,
      coachingHealthFactualContextV2Enabled,
      accessFingerprintChanged,
      isAccessRefreshPending,
      isDirectoryFetching,
      profileIncludesHardGatedLeader,
      profileIncludesUnlinkedLeader,
    ],
  );

  useEffect(() => {
    const previous = acceptedAccessFingerprintRef.current;
    if (previous === null) {
      acceptedAccessFingerprintRef.current = scopedAccessFingerprint;
    }
    const fingerprintChanged = previous !== null && previous !== scopedAccessFingerprint;
    if (
      (!fingerprintChanged &&
        !profileIncludesHardGatedLeader &&
        !profileIncludesUnlinkedLeader) ||
      !user?.id
    ) return;

    acceptedAccessFingerprintRef.current = scopedAccessFingerprint;
    const generation = accessRefreshGenerationRef.current + 1;
    accessRefreshGenerationRef.current = generation;
    setAccessRefreshPending(true);
    void Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["coach-leaders-command", user.id],
      }),
      queryClient.invalidateQueries({
        queryKey: coachHealthProfileQueryKey(user.id),
      }),
    ])
      .catch(() => undefined)
      .finally(() => {
        if (
          isMountedRef.current &&
          accessRefreshGenerationRef.current === generation
        ) {
          setAccessRefreshPending(false);
        }
      });
  }, [
    profileIncludesHardGatedLeader,
    profileIncludesUnlinkedLeader,
    queryClient,
    scopedAccessFingerprint,
    user?.id,
  ]);

  useEffect(() => {
    if (coachHealthAccessSuppressed || !coachHealthPresentation.workspace) {
      setCoachHealthWorkspaceOpen(false);
    }
  }, [coachHealthAccessSuppressed, coachHealthPresentation.workspace]);

  // Subscribe to realtime updates for new leaders accepting invites
  useEffect(() => {
    if (!user) return;

    const invalidatePortfolio = () => {
      queryClient.invalidateQueries({ queryKey: ["coach-leaders-command", user.id] });
      queryClient.invalidateQueries({
        queryKey: ["coach-leader-links-ordered", user.id],
      });
      queryClient.invalidateQueries({ queryKey: coachHealthProfileQueryKey(user.id) });
    };
    const invalidateLinkedProfile = (payload: {
      new?: Record<string, unknown>;
      old?: Record<string, unknown>;
    }) => {
      const profileId = payload.new?.id ?? payload.old?.id;
      if (
        typeof profileId !== "string" ||
        !linkedLeaderIds.has(profileId)
      ) {
        return;
      }
      invalidatePortfolio();
    };

    const channel = supabase
      .channel('coach-leader-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'profiles',
        },
        invalidateLinkedProfile,
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
        },
        invalidateLinkedProfile,
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'coach_leader_links',
          filter: `coach_id=eq.${user.id}`,
        },
        invalidatePortfolio,
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'coach_leader_links',
          filter: `coach_id=eq.${user.id}`,
        },
        invalidatePortfolio,
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [linkedLeaderIds, user, queryClient]);

  const allLeaders = (data?.leaders || []).filter(
    (leader) => isDemoMode || linkedLeaderIds.has(leader.id),
  );
  const attentionItems = useMemo(() => data?.attentionItems ?? [], [data?.attentionItems]);

  // Filter leaders based on lifecycle state
  const activeLeaders = allLeaders.filter(l => l.lifecycle_state === "active" || l.lifecycle_state === "paused");
  const archivedLeaders = allLeaders.filter(l => l.lifecycle_state === "archived" || l.lifecycle_state === "completed");
  const focusFilter = getLeaderFocusFilter(searchParams);

  // Sort leaders alphabetically for the main list
  const filteredActiveLeaders = activeLeaders.filter((leader) => {
    if (focusFilter === "new-evidence") {
      return (newEvidenceByLeaderId[leader.id] || 0) > 0;
    }
    if (focusFilter === "needs-review") {
      return leader.hasPendingTranscript;
    }
    if (focusFilter === "stale") {
      return (
        leader.hasInactivityAlert ||
        (leader.daysSinceLastPractice !== null && leader.daysSinceLastPractice >= INACTIVITY_THRESHOLD_DAYS)
      );
    }
    return true;
  });
  const sortedActiveLeaders = filteredActiveLeaders.slice().sort((a, b) => a.full_name.localeCompare(b.full_name));
  const sortedArchivedLeaders = archivedLeaders.slice().sort((a, b) => a.full_name.localeCompare(b.full_name));
  const focusBannerCopy: Record<LeaderFocusFilter, string> = {
    "new-evidence": "Showing leaders with new evidence only.",
    "needs-review": "Showing leaders with transcript review work only.",
    stale: "Showing leaders who may need coach attention for stale activity.",
  };
  const emptyStateTitle: Record<LeaderFocusFilter, string> = {
    "new-evidence": "No Leaders Match This Filter",
    "needs-review": "No Review Queue Right Now",
    stale: "No Stale Leaders Right Now",
  };
  const emptyStateBody: Record<LeaderFocusFilter, string> = {
    "new-evidence": "No active leaders currently have unreviewed evidence.",
    "needs-review": "No active leaders currently have transcript review waiting.",
    stale: "No active leaders currently show stale activity or inactivity needing coach review.",
  };
  const attentionCountByLeaderId = useMemo(() => {
    const counts: Record<string, number> = {};
    attentionItems.forEach((item) => {
      counts[item.leaderId] = (counts[item.leaderId] || 0) + 1;
    });
    return counts;
  }, [attentionItems]);
  if (authLoading || isLoading || (!isDemoMode && isLeaderGateLoading)) {
    return (
      <CoachLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </CoachLayout>
    );
  }

  if (!user) {
    return (
      <CoachLayout>
        <div className="flex flex-col items-center justify-center h-screen gap-4">
          <p className="text-muted-foreground">Please sign in to continue</p>
          <Button onClick={() => navigate("/auth")}>Sign In</Button>
        </div>
      </CoachLayout>
    );
  }

  if (!isDemoMode && isLeaderGateError) {
    return (
      <CoachLayout>
        <div className="flex min-h-[60vh] items-center justify-center p-6">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Leader access is temporarily unavailable</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                We could not confirm which coaching relationships are currently available. No leader context is shown until access is confirmed.
              </p>
              <Button
                type="button"
                onClick={() => void queryClient.invalidateQueries({
                  queryKey: ["coach-leader-links-ordered", user.id],
                })}
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </CoachLayout>
    );
  }

  const coachHealthWorkspace = coachHealthAccessSuppressed
    ? null
    : coachHealthPresentation.workspace;
  const coachHealthWorkspaceVisible = isCoachHealthWorkspaceOpen && coachHealthWorkspace !== null;

  return (
    <CoachLayout
      pageWorkspace={{
        id: COACH_HEALTH_WORKSPACE_ID,
        open: coachHealthWorkspaceVisible,
        onRequestClose: () => setCoachHealthWorkspaceOpen(false),
        content: coachHealthWorkspace ? (
          <CoachHealthReflectionWorkspace
            workspaceId={COACH_HEALTH_WORKSPACE_ID}
            presentation={coachHealthWorkspace}
            onClose={() => setCoachHealthWorkspaceOpen(false)}
            onRetry={() => void coachHealth.retry()}
          />
        ) : null,
      }}
    >
      <div className="space-y-8 p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
          <div>
            <h1 className="text-h1 text-foreground">Leaders</h1>
            <p className="text-muted-foreground">
              {activeLeaders.length} active leader{activeLeaders.length !== 1 ? "s" : ""}
              {archivedLeaders.length > 0 && ` • ${archivedLeaders.length} archived`}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">{coachRuntimeSurface.summary}</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <Button
              type="button"
              variant="outline"
              className="hidden md:inline-flex"
              aria-label="Open Coach Veera workspace"
              onClick={() => navigate("/coach/ai")}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Coach Veera
            </Button>
            {/* Invite Leader Button */}
            <CoachLeaderInviteFlow
              navigateOnSuccess={false}
              onInviteSent={() => void queryClient.invalidateQueries({ queryKey: ["coach-invitations", user.id] })}
              trigger={
                <Button>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Invite Leader
                </Button>
              }
            />
            
            {archivedLeaders.length > 0 && (
              <div className="flex items-center gap-2">
                <Switch
                  id="show-archived"
                  checked={showArchived}
                  onCheckedChange={setShowArchived}
                />
                <Label htmlFor="show-archived" className="text-sm text-muted-foreground cursor-pointer">
                  Show archived
                </Label>
              </div>
            )}
          </div>
        </div>
        {focusFilter && (
          <div className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/5 px-3 py-2">
            <p className="text-sm text-primary">{focusBannerCopy[focusFilter]}</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const nextParams = new URLSearchParams(searchParams);
                nextParams.delete("focus");
                setSearchParams(nextParams, { replace: true });
              }}
            >
              Clear filter
            </Button>
          </div>
        )}
        {!coachHealthAccessSuppressed && coachHealthPresentation.compact ? (
          <CoachHealthRecordedContextRow
            compact={coachHealthPresentation.compact}
            controlsId={COACH_HEALTH_WORKSPACE_ID}
            open={coachHealthWorkspaceVisible}
            onOpen={() => setCoachHealthWorkspaceOpen(true)}
            onRetry={() => void coachHealth.retry()}
          />
        ) : null}
        {/* Upgrade Banner */}
        {isOverLimit && leaderLimit !== null && (
          <UpgradeBanner
            leaderCount={leaderCount}
            leaderLimit={leaderLimit}
            plan={plan}
            overBy={overBy}
            softGatedCount={softGatedCount}
            hardGatedCount={hardGatedCount}
          />
        )}
        {!isDemoMode ? <CoachInvitationsCard key={user.id} coachId={user.id} linkedLeaders={allLeaders} /> : null}
        {/* Active Leaders List */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-h3 font-semibold text-foreground">Active Leaders</h2>
          </div>
          <p className="mb-4 max-w-3xl text-sm text-muted-foreground">
            This is the coach queue for leader transcript review. See which leaders have transcripts ready, then open the leader profile to review that transcript in full context.
          </p>

          {sortedActiveLeaders.length > 0 ? (
            <div className="space-y-2">
              {sortedActiveLeaders.map((leader) => {
                const gateInfo = gatedLeaders.get(leader.id);
                const isHardGated = gateInfo?.isHardGated ?? false;
                const isSoftGated = gateInfo && !gateInfo.isHardGated;
                const rowViewModel = buildCoachLeaderListRowViewModel({
                  leaderId: leader.id,
                  behavior: leader.behavior
                    ? {
                        name: leader.behavior.name,
                        phase: leader.behavior.phase,
                        last_practice_date: leader.behavior.last_practice_date,
                      }
                    : null,
                  hasPendingTranscript: leader.hasPendingTranscript,
                  pendingTranscriptCount: leader.pendingTranscriptCount,
                  pendingTranscriptId: leader.latestPendingTranscriptId,
                  newEvidenceCount: newEvidenceByLeaderId[leader.id] || 0,
                });
                const behaviorName = leader.behavior?.name || "Leader coaching";
                const coachVeeraRoute = buildCoachVeeraRoute({
                  leaderId: leader.id,
                  contextType: "leader",
                  contextualEntry: buildCoachVeeraContextualEntry({
                    surface: "leader_card",
                    coachId: user.id,
                    contextType: "leader",
                    leaderId: leader.id,
                    leaderName: leader.full_name,
                    primaryEntityId: leader.id,
                    topicTitle: behaviorName,
                    topicFingerprint: `leader-card-${behaviorName}`,
                    sourceLabel: "From Leaders",
                    carryOverSummary: `${leader.full_name}: ${behaviorName} is the thread we want to continue.`,
                    whyItMattersNow: leader.hasPendingTranscript
                      ? "Fresh transcript evidence is available, so this is a good moment to deepen the read."
                      : "This is the next most useful leader thread to deepen right now.",
                    evidenceStrength: leader.hasPendingTranscript ? "strong" : "thin",
                    question: "What is the highest-leverage next coaching move?",
                  }),
                });
                return (
                <Card
                  key={leader.id}
                  className={`transition-shadow ${isHardGated ? "opacity-40 pointer-events-none" : isSoftGated ? "opacity-75 hover:shadow-sm cursor-pointer" : "hover:shadow-sm cursor-pointer"}`}
                  onClick={() => !isHardGated && navigate(`/coach/leaders/${leader.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      {/* Leader Info */}
                      <div className="flex items-center gap-4">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                            {getAvatarInitials(leader.full_name, leader.email)}
                          </AvatarFallback>
                        </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-foreground">{leader.full_name}</p>
                              {gateInfo && <GracePeriodBadge gatedLeader={gateInfo} />}
                              {(attentionCountByLeaderId[leader.id] || 0) > 0 && (
                                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold leading-none text-primary-foreground">
                                  {attentionCountByLeaderId[leader.id]}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            {leader.behavior ? (
                              <>
                                <span>{rowViewModel.behaviorLabel}</span>
                                <span>•</span>
                                <span>{rowViewModel.phaseLabel}</span>
                              </>
                            ) : (
                              <span className="text-muted-foreground/60">No active behavior</span>
                            )}
                            </div>
                            {rowViewModel.newEvidenceLabel && (
                              <p className="text-xs text-primary mt-1">
                                {rowViewModel.newEvidenceLabel}
                              </p>
                            )}
                            {"hasPendingBillingResolution" in leader && leader.hasPendingBillingResolution && (
                              <div className="mt-2 inline-flex max-w-xl items-center gap-2 rounded-full border border-amber-300/50 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-700">
                                {buildCoachBillingPendingCopy()}
                              </div>
                            )}
                            {rowViewModel.transcriptBadgeLabel && (
                              <div className="mt-2">
                                <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/50 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-700">
                                  <FileText className="h-3.5 w-3.5" />
                                  {rowViewModel.transcriptBadgeLabel}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                      {/* Stats & Actions */}
                      <div className="flex items-center gap-6">
                        {leader.behavior && (
                          <>
                            {/* Last recorded moment */}
                            <div className="text-right hidden sm:block">
                              <p className="text-xs text-muted-foreground">Last recorded moment</p>
                              <p className="text-sm font-medium">
                                {leader.behavior.last_practice_date
                                  ? formatDistanceToNow(new Date(leader.behavior.last_practice_date), { addSuffix: true })
                                  : "Never"}
                              </p>
                            </div>

                            {/* Enrichment Status */}
                            <div className="hidden md:block">
                              {leader.hasEnrichment ? (
                                <Badge
                                  variant="outline"
                                  className="bg-purple-50 text-purple-700 border-purple-200"
                                  title="Veera has onboarding context (best-self narrative or meaning anchor) for this leader"
                                >
                                  <Sparkles className="h-3 w-3 mr-1" />
                                  Enriched
                                </Badge>
                              ) : (
                                <button
                                  type="button"
                                  className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline transition-colors"
                                  title="No onboarding context yet — open the leader to get capture set up"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/coach/leaders/${leader.id}`);
                                  }}
                                >
                                  Add context
                                </button>
                              )}
                            </div>
                          </>
                        )}

                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground"
                            aria-label={`Coach Veera for ${leader.full_name}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(coachVeeraRoute);
                            }}
                          >
                            Coach Veera
                          </Button>
                          <Button
                            variant={leader.hasPendingTranscript ? "secondary" : "ghost"}
                            size="sm"
                            className={leader.hasPendingTranscript ? "min-h-11" : "text-muted-foreground"}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(rowViewModel.actionRoute);
                            }}
                          >
                            {rowViewModel.actionLabel}
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-h3 text-foreground mb-2">
                  {focusFilter ? emptyStateTitle[focusFilter] : "No Active Leaders Yet"}
                </h3>
                <p className="text-muted-foreground">
                  {focusFilter
                    ? emptyStateBody[focusFilter]
                    : "Leaders appear here after accepting your invitation. Track outstanding invitations above."}
                </p>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Archived Leaders (shown when toggle is on) */}
        {showArchived && sortedArchivedLeaders.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Archive className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-h3 font-semibold text-muted-foreground">Archived Leaders</h2>
            </div>

            <div className="space-y-2">
              {sortedArchivedLeaders.map((leader) => (
                <Card
                  key={leader.id}
                  className="hover:shadow-sm transition-shadow cursor-pointer opacity-75"
                  onClick={() => navigate(`/coach/leaders/${leader.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      {/* Leader Info */}
                      <div className="flex items-center gap-4">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-muted text-muted-foreground text-sm font-medium">
                            {getAvatarInitials(leader.full_name, leader.email)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-foreground">{leader.full_name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Badge variant="outline" className="text-xs">
                              {leader.lifecycle_state === "completed" ? "Completed" : "Archived"}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/coach/leaders/${leader.id}`);
                          }}
                        >
                          View
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>
    </CoachLayout>
  );
}
